/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { createContext, useContext, useState, useRef, useEffect, useId } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useEscapeKey } from "../../hooks/a11y/useEscapeKey";
import { isActivationKey } from "../../hooks/a11y/activationKeys";
import styled, { css } from "styled-components";
import Text from "./Text";
import { focusRingInsetStyles } from "../GlobalWidgetStyle";

const PLACEMENTS = ["top", "bottom", "bottom-left", "top-left", "bottom-right", "top-right", "left", "right"];
const OFFSET = 4;

const StyledDropdownMenu = styled.div`
  position: relative;
`;

const StyledDropdownMenuContent = styled.div`
  visibility: ${({ $isPositioned }) => ($isPositioned ? "visible" : "hidden")};
  display: ${({ $show }) => ($show ? "block" : "none")};
  position: absolute;
  border-radius: 8px;
  overflow: hidden;
  text-align: left;
  box-shadow: 2px 2px 15px rgba(${({ theme }) => theme.colors.text.rgb}, 0.1);
  background: rgba(${({ theme }) => theme.colors.surface.rgba});
  z-index: 1000;
  min-width: ${({ $minWidth }) => $minWidth || "200px"};
  max-width: ${({ $maxWidth }) => $maxWidth || "320px"};
  width: max-content;

  ${({ $placement, $triggerRect, $contentDimensions }) => {
    if (!$triggerRect) return {};

    const { width, height } = $triggerRect;

    switch ($placement) {
      case "top":
        return css`
          left: ${width / 2}px;
          top: ${-$contentDimensions.height - OFFSET}px;
        `;
      case "bottom":
        return css`
          left: ${width / 2}px;
          top: ${height + OFFSET}px;
        `;
      case "left":
        return css`
          right: ${width + OFFSET}px;
          top: ${height / 2}px;
        `;
      case "right":
        return css`
          left: ${width + OFFSET}px;
          top: ${height / 2}px;
        `;
      case "top-left":
        return css`
          left: 0;
          top: ${-$contentDimensions.height - OFFSET}px;
        `;
      case "top-right":
        return css`
          right: 0;
          top: ${-$contentDimensions.height - OFFSET}px;
        `;
      case "bottom-left":
        return css`
          left: 0;
          top: ${height + OFFSET}px;
        `;
      case "bottom-right":
        return css`
          right: 0;
          top: ${height + OFFSET}px;
        `;
      default:
        return {};
    }
  }};
`;

const StyledDropdownMenuHeader = styled.div`
  display: grid;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
`;

const StyledDropdownMenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  text-decoration: none;

  @media (hover: hover) {
    &:hover {
      background-color: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.1);
    }
  }

  &:focus-visible {
    ${focusRingInsetStyles}
  }

  ${({ $variant, theme }) =>
    $variant === "danger" &&
    css`
      @media (hover: hover) {
        &:hover {
          background-color: rgba(${theme.colors.error.rgb}, 0.1);
        }
      }
    `}
`;

const StyledDropdownMenuDivider = styled.div`
  height: 1px;
  background-color: rgba(${({ theme }) => theme.colors.border.rgba});
`;

const DropdownMenuContext = createContext();

const DropdownMenuProvider = ({ children, placement, closeOnSelect }) => {
  if (!PLACEMENTS.includes(placement)) {
    throw new Error(`Invalid placement: ${placement}. Must be one of ${PLACEMENTS.join(", ")}`);
  }

  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const contentId = useId();

  useClickOutside(() => setIsOpen(false), [triggerRef.current, contentRef.current]);

  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((prev) => !prev);

  useEscapeKey(close, isOpen);

  return (
    <DropdownMenuContext.Provider
      value={{ isOpen, setIsOpen, triggerRef, contentRef, contentId, placement, close, toggle, closeOnSelect }}
    >
      {children}
    </DropdownMenuContext.Provider>
  );
};

const useDropdownMenu = () => {
  const context = useContext(DropdownMenuContext);
  if (!context) {
    throw new Error("useDropdownMenu must be used within a DropdownMenuProvider");
  }
  return context;
};

const DropdownMenuTrigger = ({ children }) => {
  const { toggle, setIsOpen, triggerRef, isOpen, contentId } = useDropdownMenu();

  return React.cloneElement(children, {
    ref: triggerRef,
    "aria-haspopup": "menu",
    "aria-expanded": isOpen,
    "aria-controls": contentId,
    onClick: (e) => {
      if (children.props.onClick) {
        e.stopPropagation();
        children.props.onClick(e);
      }
      toggle();
    },
    onKeyDown: (e) => {
      children.props.onKeyDown?.(e);

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setIsOpen(true);
      }
    },
  });
};

const DropdownMenuContent = ({ children, minWidth, maxWidth }) => {
  const { isOpen, contentRef, contentId, placement, triggerRef, close } = useDropdownMenu();
  const [triggerRect, setTriggerRect] = useState(null);
  const [contentDimensions, setContentDimensions] = useState(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const updateDimensions = () => {
      if (!contentRef.current || !triggerRef.current) return;

      setContentDimensions(contentRef.current.getBoundingClientRect());
      setTriggerRect(triggerRef.current.getBoundingClientRect());
    };

    if (isOpen) {
      updateDimensions();
      window.addEventListener("resize", updateDimensions);
    }

    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, [isOpen, contentRef, triggerRef]);

  const getMenuItems = () =>
    contentRef.current ? Array.from(contentRef.current.querySelectorAll('[role="menuitem"]')) : [];
  const isPositioned = !!(isOpen && triggerRect && contentDimensions);

  useEffect(() => {
    if (isPositioned) {
      getMenuItems()[0]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPositioned]);

  useEffect(() => {
    if (!isOpen && wasOpenRef.current) {
      triggerRef.current?.focus();
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, triggerRef]);

  const handleKeyDown = (event) => {
    const items = getMenuItems();
    if (items.length === 0) return;

    const currentIndex = items.indexOf(document.activeElement);

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        items[(currentIndex + 1) % items.length].focus();
        break;
      case "ArrowUp":
        event.preventDefault();
        items[currentIndex <= 0 ? items.length - 1 : currentIndex - 1].focus();
        break;
      case "Home":
        event.preventDefault();
        items[0].focus();
        break;
      case "End":
        event.preventDefault();
        items[items.length - 1].focus();
        break;
      case "Tab":
        close();
        break;
      default:
        break;
    }
  };

  return (
    <StyledDropdownMenuContent
      ref={contentRef}
      id={contentId}
      role="menu"
      onKeyDown={handleKeyDown}
      $placement={placement}
      $show={isOpen}
      $isPositioned={isPositioned}
      $triggerRect={triggerRect}
      $contentDimensions={contentDimensions}
      $minWidth={minWidth}
      $maxWidth={maxWidth}
    >
      {isOpen ? children : null}
    </StyledDropdownMenuContent>
  );
};

const DropdownMenuHeader = ({ children }) => {
  return <StyledDropdownMenuHeader>{children}</StyledDropdownMenuHeader>;
};

const DropdownMenuItem = ({ title, icon, onClick, variant, as: Component, ...props }) => {
  const { close, closeOnSelect } = useDropdownMenu();

  const handleClick = (e) => {
    onClick?.(e);
    if (closeOnSelect) {
      close();
    }
  };

  const itemProps = {
    onClick: handleClick,
    role: "menuitem",
    $variant: variant,
    ...props,
  };

  const textColor = variant === "danger" ? "error" : "dark";

  const content = (
    <>
      {icon}
      {title && <Text color={textColor}>{title}</Text>}
    </>
  );

  if (Component) {
    return (
      <StyledDropdownMenuItem as={Component} {...itemProps}>
        {content}
      </StyledDropdownMenuItem>
    );
  }

  const handleKeyDown = (e) => {
    if (isActivationKey(e)) {
      e.preventDefault();
      handleClick(e);
    }
  };

  return (
    <StyledDropdownMenuItem tabIndex={0} onKeyDown={handleKeyDown} {...itemProps}>
      {content}
    </StyledDropdownMenuItem>
  );
};

const DropdownMenuDivider = () => {
  return <StyledDropdownMenuDivider />;
};

const DropdownMenu = ({ children, placement = "bottom", closeOnSelect = false }) => (
  <DropdownMenuProvider placement={placement} closeOnSelect={closeOnSelect}>
    <StyledDropdownMenu>{children}</StyledDropdownMenu>
  </DropdownMenuProvider>
);

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuHeader,
  DropdownMenuItem,
  DropdownMenuDivider,
  useDropdownMenu,
};
